import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Switch } from "@/components/ui/switch"
import { Checkbox } from "@/components/ui/checkbox"
import { useState, useEffect } from "react"

const DAYS = [
  { id: "saturday", label: "Saturday" },
  { id: "sunday", label: "Sunday" },
  { id: "monday", label: "Monday" },
  { id: "tuesday", label: "Tuesday" },
  { id: "wednesday", label: "Wednesday" },
  { id: "thursday", label: "Thursday" },
  { id: "friday", label: "Friday" },
]

export function AddScheduleDialog({
  open,
  onOpenChange,
  onSubmit,
  doctorId,
  existingSchedule,
}) {
  const [isOnCall, setIsOnCall] = useState(false)
  const [selectedDays, setSelectedDays] = useState([])
  const [daySchedules, setDaySchedules] = useState({})

  // Reset form when dialog opens/closes or when existingSchedule changes
  useEffect(() => {
    if (open && existingSchedule) {
      setIsOnCall(existingSchedule.is_oncall || false)

      if (existingSchedule.is_oncall) {
        setSelectedDays([])
        setDaySchedules({})
      } else {
        const daysWithSchedule = DAYS.map((day) => day.id).filter(
          (day) => existingSchedule[day]
        )

        setSelectedDays(daysWithSchedule)
        const schedules = daysWithSchedule.reduce(
          (acc, day) => ({
            ...acc,
            [day]: {
              morning: existingSchedule[day]?.morning || "",
              afternoon: existingSchedule[day]?.afternoon || "",
              evening: existingSchedule[day]?.evening || "",
            },
          }),
          {}
        )
        setDaySchedules(schedules)
      }
    } else if (!open) {
      setIsOnCall(false)
      setSelectedDays([])
      setDaySchedules({})
    }
  }, [open, existingSchedule])

  const handleOnCallChange = (checked) => {
    setIsOnCall(checked)
    if (checked) {
      // Clear all day schedules when switching to on-call
      setSelectedDays([])
      setDaySchedules({})
    }
  }

  const handleDayToggle = (day) => {
    setSelectedDays((prev) => {
      if (prev.includes(day)) {
        const newSelected = prev.filter((d) => d !== day)
        setDaySchedules((prev) => {
          const { [day]: _, ...rest } = prev
          return rest
        })
        return newSelected
      }
      return [...prev, day]
    })

    if (!daySchedules[day]) {
      setDaySchedules((prev) => ({
        ...prev,
        [day]: { morning: "", afternoon: "", evening: "" },
      }))
    }
  }

  const handleScheduleChange = (day, period, value) => {
    setDaySchedules((prev) => ({
      ...prev,
      [day]: { ...prev[day], [period]: value || null },
    }))
  }

  const handleSubmit = (e) => {
    e.preventDefault()

    const scheduleData = {
      is_oncall: isOnCall,
      ...DAYS.reduce(
        (acc, { id }) => ({
          ...acc,
          [id]: isOnCall
            ? null
            : selectedDays.includes(id)
              ? {
                  morning: daySchedules[id]?.morning || null,
                  afternoon: daySchedules[id]?.afternoon || null,
                  evening: daySchedules[id]?.evening || null,
                }
              : null,
        }),
        {}
      ),
    }

    onSubmit(scheduleData)
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[525px]">
        <DialogHeader>
          <DialogTitle>Add Schedule</DialogTitle>
          <DialogDescription>
            Set the doctor's availability schedule.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="grid gap-4 py-4">
            <div className="flex items-center justify-between">
              <Label htmlFor="on-call">On Call</Label>
              <Switch
                id="on-call"
                checked={isOnCall}
                onCheckedChange={handleOnCallChange}
              />
            </div>

            {!isOnCall && (
              <div className="grid gap-4">
                {DAYS.map(({ id, label }) => (
                  <div key={id} className="space-y-4">
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id={id}
                        checked={selectedDays.includes(id)}
                        onCheckedChange={() => handleDayToggle(id)}
                      />
                      <Label htmlFor={id}>{label}</Label>
                    </div>

                    {selectedDays.includes(id) && (
                      <div className="grid grid-cols-3 gap-4 ml-6">
                        <div className="space-y-2">
                          <Label>Morning</Label>
                          <Input
                            placeholder="9am - 12pm"
                            value={daySchedules[id]?.morning || ""}
                            onChange={(e) =>
                              handleScheduleChange(
                                id,
                                "morning",
                                e.target.value
                              )
                            }
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Afternoon</Label>
                          <Input
                            placeholder="2pm - 5pm"
                            value={daySchedules[id]?.afternoon || ""}
                            onChange={(e) =>
                              handleScheduleChange(
                                id,
                                "afternoon",
                                e.target.value
                              )
                            }
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Evening</Label>
                          <Input
                            placeholder="6pm - 9pm"
                            value={daySchedules[id]?.evening || ""}
                            onChange={(e) =>
                              handleScheduleChange(
                                id,
                                "evening",
                                e.target.value
                              )
                            }
                          />
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button type="submit">Save Schedule</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
